// ================================================================================================
import { Component, ReactNode, CSSProperties, Fragment } from 'react';
import _ from "lodash";
import { saveAs } from "file-saver";
import { Macros } from '@ModelViews/ModelViews';
import { BoardMacroEngine, BoardMacroEngineLogInstance, MacroType, MacroInfoEx } from "./BoardMacroEngine";
import { getZDepthShadow } from "@Utils/Colors";
import { isDefined, JSONSafeStringify } from "@Utils/Micro";
import { TypoLabel } from "@UI/Primitive/Typography";
import { RenderToolbar } from '@UI/Layout/AdvanceLayout';
import { ReactJsonView } from '@Primitive/ReactJsonView';
import { FolderItemType, FolderStructure } from '@Primitive/TreeStructure';
import { IComponentSizeProp } from '@Primitive/Interfaces';
import { TreeStructureLite } from '@Primitive/List/TreeStructureLite';
import { ToolbarIconButton } from '@Primitive/ToolbarIconButton';
import { GetGemTypeIcon } from "./BoardMacroUtils";
import { Panel } from 'rsuite';
import { Props } from '@Utils/ReactProps';
import { yHas } from '@Utils/Utils';
// ================================================================================================
export interface BoardMacroDbgViewProp extends Props<BoardMacroDbgView>, IComponentSizeProp {
    NodeKey: string;
    onOpenEditor?: (macroGuid: string) => void
}
export interface BoardMacroDbgViewState {
    hideEarlyExitGems: boolean;
    SelectedMacroGuid: string;
    MacroFolderState: { [key: string]: boolean };
}
const enum GemsPagesKeys {
    Register = "__register__",
    Timeline = "__timeline__"
}
// ------------------------------------------------------------------------------------------------
export class BoardMacroDbgView extends Component<BoardMacroDbgViewProp, BoardMacroDbgViewState> {
    constructor(props: BoardMacroDbgViewProp) {
        super(props);
        this.state = {
            SelectedMacroGuid: GemsPagesKeys.Timeline,
            MacroFolderState: {},
            hideEarlyExitGems: true
        };
    }
    // --------------------------------------------------------------------------------------------
    renderResultsJS(log: BoardMacroEngineLogInstance) {
        return <Fragment>
            {isDefined(log.Error) && log.Error.length > 0
                ? <Fragment>
                    <TypoLabel>Error:</TypoLabel>
                    <br />
                    <ReactJsonView
                        key={"JsHandlingResults"}
                        value={log.Error}
                        style={{
                            margin: "4px 0px"
                        }}
                    />
                    <br />
                </Fragment>
                : null}
            <TypoLabel>JS Input:</TypoLabel>
            <br />
            <ReactJsonView
                key={"JSPrepInput"}
                value={log.JSPrepInput}
                style={{ margin: "4px 0px" }}
            />
            <TypoLabel style={{ marginTop: 15 }}>JS Results (Exec: {Math.round(log.Performance?.JSPrepExec)} Overall: {Math.round(log.Performance?.JSPrep)}ms):</TypoLabel>
            <br />
            <ReactJsonView
                key={"JsPrepResults"}
                value={log.JsPrepResults}
                style={{ margin: "4px 0px" }}
            />
            <TypoLabel style={{ marginTop: 15 }}>Data Destination:</TypoLabel>
            <br />
            <ReactJsonView
                key={"JsHandlingDst"}
                value={log.JsHandlingDst}
                style={{ margin: "4px 0px" }}
            />
        </Fragment>
    }
    // --------------------------------------------------------------------------------------------
    renderResultsQuery(log: BoardMacroEngineLogInstance) {
        return <Fragment>
            {isDefined(log.Error) && log.Error.length > 0
                ? <Fragment>
                    <TypoLabel>Error:</TypoLabel>
                    <br />
                    <ReactJsonView
                        key={"JsHandlingResults"}
                        value={log.Error}
                        style={{
                            margin: "4px 0px"
                        }}
                    />
                    <br />
                </Fragment>
                : null}
            <TypoLabel>JS Prep Input:</TypoLabel>
            <br />
            <ReactJsonView
                key={"JSPrepInput"}
                value={log.JSPrepInput}
                style={{ margin: "4px 0px" }}
            />
            <TypoLabel style={{ marginTop: 15 }}>JS Prep Results (Exec: {Math.round(log.Performance?.JSPrepExec)} Overall: {Math.round(log.Performance?.JSPrep)}ms):</TypoLabel>
            <br />
            <ReactJsonView
                key={"JsPrepResults"}
                value={log.JsPrepResults}
                style={{ margin: "4px 0px" }}
            />
            <TypoLabel style={{ marginTop: 15 }}>Query Results ({Math.round(log.Performance?.Query)}ms):</TypoLabel>
            <br />
            <ReactJsonView
                key={"QueryResults"}
                value={log.QueryResults}
                style={{ margin: "4px 0px" }}
            />
            <TypoLabel style={{ marginTop: 15 }}>JS Handling Results (Exec: {Math.round(log.Performance?.JSHandlingExec)} Overall: {Math.round(log.Performance?.JSHandling)}ms):</TypoLabel>
            <br />
            <ReactJsonView
                key={"JsHandlingResults"}
                value={log.JsHandlingResults}
                style={{ margin: "4px 0px" }}
            />
            <TypoLabel style={{ marginTop: 15 }}>Data Destination:</TypoLabel>
            <br />
            <ReactJsonView
                key={"JsHandlingDst"}
                value={log.JsHandlingDst}
                style={{ margin: "4px 0px" }}
            />
        </Fragment>
    }
    // --------------------------------------------------------------------------------------------
    renderMacroTree() {
        let engine = BoardMacroEngine.GetInstance(this.props.NodeKey);
        if (!isDefined(engine))
            return null;
        let macros = engine.MacroDebugHistory;

        const pageSelectedStyle: CSSProperties = {
            color: "rgb(0, 157, 119)",
            fontWeight: 'bold',
            backgroundColor: "rgba(0,0,0,0.1)",
            borderLeft: "4px solid " + "rgb(0, 157, 119)"
        };

        // hide early exit executions
        let treeMacros = _.values(macros);
        if (this.state.hideEarlyExitGems)
            treeMacros = treeMacros.filter(x => _.findIndex(x.Log, l => l.JsPrepResults != null || (isDefined(l.Error) && l.Error.length > 0)) > -1);

        // create folders
        let structure = TreeStructureLite.helperPathSplitter(
            treeMacros,
            "Macro.Settings.Path",
            (log, path, id) => {
                let item = log.Macro;
                let iconUrl = GetGemTypeIcon(item.MacroType);

                let leftIcon = [<img key={`${id}-icon`} src={iconUrl} width={24} height={24} />];
                if (item.Settings.Type == MacroType.Less)
                    leftIcon = [<i key={`${id}-less`} className="fab fa-less" style={{ paddingLeft: 10 }} />]
                else if (item.Settings.Type == MacroType.Init)
                    leftIcon.push(<i key={`${id}-meteor`} className="fas fa-meteor" style={{ paddingLeft: 10 }} />)

                return {
                    _id: id,
                    key: item.Guid,
                    path: path,
                    primaryText: item.Settings.Name,
                    secondaryText: item.Settings.Description,
                    parentId: 0,
                    type: FolderItemType.Dashboard,
                    children: [],
                    style: (this.state.SelectedMacroGuid == item.Guid) ? pageSelectedStyle : {},
                    leftIcon: leftIcon,
                    rightIcon: <div key={`${id}-right-icon`} style={{
                        marginRight: 8, marginTop: 6,
                        borderRadius: 24, width: 24, height: 24,
                        backgroundColor: log.LastStateHaveErrors ? "#e53935" : "#039BE5",
                        textAlign: "center",
                        fontSize: 16, color: "white"
                    }}>
                        {log.Log.length}
                    </div>
                } as FolderStructure
            },
            (filename, path, id) => {
                let isOpen = this.state.MacroFolderState[path];
                if (!isDefined(isOpen)) {
                    this.state.MacroFolderState[path] = true;
                    isOpen = true;
                }

                return {
                    _id: id, children: [], path: path, key: path,
                    type: FolderItemType.Folder, primaryText: filename, parentId: 0,
                    leftFontIcon: (isOpen) ? ["fa-folder-open"] : ["fa-folder"],
                    open: isOpen,
                };
            });

        structure.unshift({
            _id: 10000,
            key: GemsPagesKeys.Timeline,
            path: "",
            primaryText: "Timeline",
            parentId: 0,
            type: FolderItemType.Dashboard,
            children: [],
            style: (this.state.SelectedMacroGuid == GemsPagesKeys.Timeline) ? pageSelectedStyle : {},
            leftIcon: <i className="fas fa-list-alt center" style={{ color: "#73b129" }} />,
        });

        structure.unshift({
            _id: 10000,
            key: GemsPagesKeys.Register,
            path: "",
            primaryText: "Register",
            parentId: 0,
            type: FolderItemType.Dashboard,
            children: [],
            style: (this.state.SelectedMacroGuid == GemsPagesKeys.Register) ? pageSelectedStyle : {},
            leftIcon: <i className='fas fa-memory center' style={{ color: "#039BE5" }} />,
        });

        return <TreeStructureLite
            secondaryTextLimitLine={2}
            FolderStructure={structure}
            onListItemSelected={(key) => this.setState({
                SelectedMacroGuid: key
            })}
            onNestedListToggle={(id: number, key: string) => {
                this.setState((s) => {
                    if (!yHas(s.MacroFolderState, key)) {
                        s.MacroFolderState[key] = true;
                    } else {
                        s.MacroFolderState[key] = !s.MacroFolderState[key];
                    }
                    return s;
                });
            }}
        />
    }
    // --------------------------------------------------------------------------------------------
    renderTimeline() {
        let engine = BoardMacroEngine.GetInstance(this.props.NodeKey);
        if (!isDefined(engine))
            return null;

        let macrosLogs = _(engine.MacroDebugHistory)
            .filter(e => !this.state.hideEarlyExitGems ||
                (_.findIndex(e.Log, l => l.JsPrepResults != null || (isDefined(l.Error) && l.Error.length > 0)) > -1))
            .flatMap(e => {
                return _(e.Log)
                    .filter(l => !this.state.hideEarlyExitGems || l.JsPrepResults != null || (isDefined(l.Error) && l.Error.length > 0))
                    .map((l, index) => {
                        let nl: BoardMacroEngineLogInstance & { Macro?: MacroInfoEx, i?: number } = _.clone(l);
                        nl.Macro = e.Macro;
                        nl.i = index;
                        return nl;
                    })
                    .value();
            })
            .orderBy(x => x.Timestamp, "desc")
            .value();

        return <div>
            {_.map(macrosLogs, l => {
                // Set useful flags.
                const hasError: boolean = isDefined(l.Error) && l.Error.length > 0;

                // extract icon 
                let iconUrl = GetGemTypeIcon(l.Macro.MacroType);
                let leftIcon = <img
                    src={iconUrl}
                    width={40}
                    height={40}
                    style={{
                        backgroundColor: "#262822",
                        padding: 4,
                        borderRadius: 10,
                        position: 'absolute'
                    }} />;

                // Set card's header subtitle.
                const subtitle: ReactNode = <div style={{
                    marginLeft: 60,
                    display: 'grid',
                    justifyItems: 'start',
                    gap: 2,
                    marginTop: 5
                }}>
                    <small>{"Completion Timestamp: " + l.Timestamp}</small>
                    {l.Macro.Settings.Type == MacroType.Init ? <small>Init Gem</small> : null}
                    {l.Macro.Settings.Type == MacroType.Less ? <small>Less styling Gem</small> : null}
                    {isDefined(l.Performance)
                        ? <small>{'Performance: ' + Math.round(l.Performance.JSHandling + l.Performance.Query + l.Performance.JSPrep) + "ms"}</small>
                        : null}
                    {isDefined(l.Performance)
                        ? <small>{'PerformanceExec: ' + Math.round(l.Performance.JSHandlingExec + l.Performance.Query + l.Performance.JSPrepExec) + "ms"}</small>
                        : null}
                    {isDefined(l.Performance) && l.Performance.Query > 0
                        ? <small>{'Query Time: ' + Math.round(l.Performance.Query) + "ms"}</small>
                        : null
                    }
                    {_.map(l.Error, (e, i) => <small key={`error-${i}`}>
                        <i className='fas fa-exclamation-circle center' style={{
                            width: 18,
                            height: 18,
                            verticalAlign: 'sub',
                            color: '#e53935'
                        }} />
                        {'[' + e.from + "] " + e.msg}
                    </small>)}
                    {l.StoppedFromUser ? <small>Execution stopped from user</small> : null}
                </div>;

                return <Panel
                    bordered
                    collapsible
                    key={l.Macro.Guid + " - " + l.i}
                    header={<>
                        {leftIcon}
                        <div style={{ display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'start' }}>
                            <div
                                style={{
                                    color: hasError ? "#da0500" : (l.JsPrepResults != null ? "#1964b9" : "#965e03"),
                                    fontWeight: 700,
                                    marginLeft: 60
                                }}>
                                {l.Macro.Settings.Path + "/" + l.Macro.Settings.Name}
                            </div>
                            {subtitle}
                        </div>
                    </>}
                    style={{
                        margin: 5,
                        borderRadius: 10,
                        backgroundColor: "rgba(255, 255, 255, 0.9)"
                    }}>
                    <RenderToolbar
                        height={35}
                        style={{ backgroundColor: "white", color: "black", boxShadow: "unset" }}
                        RightElements={[<ToolbarIconButton
                            key="open"
                            tooltip="Open Gem Editor"
                            className="fas fa-laptop-code"
                            onClick={() => this.openEditorSelected(l.Macro.Guid)} />]}
                    />
                    {(l.Macro.MacroType == Macros.MacroScriptType.Javascript) ?
                        this.renderResultsJS(l) :
                        this.renderResultsQuery(l)}
                </Panel>;
            })}
        </div>
    }
    // --------------------------------------------------------------------------------------------
    renderSelectedMacro() {
        let engine = BoardMacroEngine.GetInstance(this.props.NodeKey);
        if (!isDefined(engine))
            return null;

        // Register
        if (this.state.SelectedMacroGuid == GemsPagesKeys.Register)
            return <div style={{ overflow: "hidden", borderRadius: 10, margin: 5 }}>
                <ReactJsonView
                    key={"json-viewer-output"}
                    value={engine.Register} />
            </div>

        // Timeline
        if (this.state.SelectedMacroGuid == GemsPagesKeys.Timeline)
            return this.renderTimeline();

        let selected = _.find(engine.MacroDebugHistory,
            x => x.Macro.Guid == this.state.SelectedMacroGuid &&
                (!this.state.hideEarlyExitGems ||
                    _.findIndex(x.Log, l => l.JsPrepResults != null || (isDefined(l.Error) && l.Error.length > 0)) > -1));
        if (!isDefined(selected))
            return null;

        return _(selected.Log)
            .filter(l => !this.state.hideEarlyExitGems || l.JsPrepResults != null || (isDefined(l.Error) && l.Error.length > 0))
            .map(l => {
                // Set useful flags.
                const hasError: boolean = isDefined(l.Error) && l.Error.length > 0;

                // Set card's header subtitle.
                const subtitle: ReactNode = <div style={{ display: 'grid' }}>
                    {isDefined(selected.Log[0].Performance)
                        ? <small>{'Performance: ' + Math.round(_.sum(_.values(selected.Log[0].Performance))) + "ms"}</small>
                        : null}
                    {_.map(l.Error, e => <small>
                        <i className='fas fa-exclamation-circle center' style={{
                            width: 18,
                            height: 18,
                            verticalAlign: 'sub',
                            color: '#e53935'
                        }} />
                        {'[' + e.from + "] " + e.msg}
                    </small>)}
                </div>;

                return <Panel
                    bordered
                    collapsible
                    key={l.Timestamp}
                    header={<>
                        <div
                            style={{
                                color: hasError ? "#da0500" : (l.JsPrepResults != null ? "#1964b9" : "#965e03"),
                                fontWeight: 700
                            }}>
                            {l.Timestamp}
                        </div>
                        {subtitle}
                    </>}
                    style={{
                        margin: 5,
                        borderRadius: 10,
                        backgroundColor: "rgba(255, 255, 255, 0.9)"
                    }}>
                    {selected.Macro.MacroType == Macros.MacroScriptType.Javascript
                        ? this.renderResultsJS(l)
                        : this.renderResultsQuery(l)}
                </Panel>;
            })
            .value();
    }
    // --------------------------------------------------------------------------------------------
    clearLogs = () => {
        let engine = BoardMacroEngine.GetInstance(this.props.NodeKey);
        if (!isDefined(engine))
            return null;
        engine.MacroDebugHistory = {};
        this.forceUpdate();
    }
    // --------------------------------------------------------------------------------------------
    downloadLogs = () => {
        let engine = BoardMacroEngine.GetInstance(this.props.NodeKey);
        if (!isDefined(engine))
            return null;
        let logs = {};
        _.forEach(engine.MacroDebugHistory, (h, key) => {
            logs[key] = {
                Type: h.Macro.MacroType,
                Name: h.Macro.Settings.Name,
                Path: h.Macro.Settings.Path,
                Logs: h.Log
            };
        });
        let data = JSONSafeStringify(logs, undefined, "\t");
        saveAs(new Blob([data], { type: "text/plain;charset=utf-8" }), "MacroLog.json");
    }
    // --------------------------------------------------------------------------------------------
    openEditor = () => this.openEditorSelected(this.state.SelectedMacroGuid)
    // --------------------------------------------------------------------------------------------
    openEditorSelected = (guid) => {
        if (guid != null &&
            guid != GemsPagesKeys.Timeline &&
            guid != GemsPagesKeys.Register &&
            this.props.onOpenEditor != null)
            this.props.onOpenEditor(guid);
    }
    // --------------------------------------------------------------------------------------------
    hideEarlyExitExecutions = () => {
        this.setState({ hideEarlyExitGems: !this.state.hideEarlyExitGems })
    }
    // --------------------------------------------------------------------------------------------
    render() {
        let bodyHeight = this.props.height - 35;
        return <Fragment>
            <RenderToolbar
                height={35}
                style={{ backgroundColor: "rgb(38, 40, 34)" }}
                RightElements={[
                    (this.state.SelectedMacroGuid != null &&
                        this.state.SelectedMacroGuid != GemsPagesKeys.Timeline &&
                        this.state.SelectedMacroGuid != GemsPagesKeys.Register) ?
                        <ToolbarIconButton key='open' tooltip="Open Gem Editor" className="fas fa-laptop-code" onClick={this.openEditor} /> : null,
                    <ToolbarIconButton
                        key='vis'
                        tooltip={this.state.hideEarlyExitGems ? "Show all executed gems" : "Hide early exit execution gems"}
                        className={this.state.hideEarlyExitGems ? "fas fa-eye-slash" : "fas fa-eye"} onClick={this.hideEarlyExitExecutions} />,
                    <ToolbarIconButton key='clear' tooltip="Clear Log" className="fas fa-trash" onClick={this.clearLogs} />,
                    <ToolbarIconButton key='download' tooltip="Download Log" className="fas fa-cloud-download-alt" onClick={this.downloadLogs} />
                ]}
                LeftElements={[]}
            />
            <div style={{
                width: this.props.width,
                height: bodyHeight,
                display: "flex",
                backgroundColor: "rgb(38, 40, 34)",
            }}>
                <div style={{
                    height: bodyHeight,
                    width: 300,
                    color: "white",
                    overflow: "auto",
                    boxShadow: getZDepthShadow(3),
                    position: "relative"
                }}>
                    {this.renderMacroTree()}
                </div>
                <div style={{
                    flex: 1,
                    height: bodyHeight,
                    overflow: "hidden auto", backgroundColor: "rgb(234, 234, 233)"
                }}>
                    {this.renderSelectedMacro()}
                </div>
            </div>
        </Fragment>
    }
    // --------------------------------------------------------------------------------------------
}